/**
 * agentLogCounts.ts — the client-facing activity totals (calls answered, posts
 * published, reviews requested, skill tasks), defined ONCE (2026-10-09).
 *
 * ⚠️ Until 2026-10-09 these were `agentLogs.filter(...).length` over a subscription
 * capped at the newest 200 logs, presented on the Dashboard and Reports as totals.
 * Measured that day: CPA Jake's "Skill Tasks" showed 80 when the true count was 196.
 * Each definition below drives BOTH the server count() query and the in-memory
 * predicate used while the count is in flight, so the two cannot disagree about
 * what is being counted.
 */
import { where, type QueryConstraint } from 'firebase/firestore'

export type AgentLogCountDef =
  | { agentId: string; taskType: string }
  | { statusIn: readonly string[] }

export const AGENT_LOG_COUNT_DEFS = {
  callsAnswered: { agentId: 'phone', taskType: 'call_answered' },
  postsPublished: { agentId: 'social', taskType: 'post_published' },
  reviewsGenerated: { agentId: 'google', taskType: 'review_request' },
  // A 'flagged' run (2026-07-14) is a COMPLETED task — the agent ran and found something.
  agentTasks: { statusIn: ['success', 'flagged'] },
} as const satisfies Record<string, AgentLogCountDef>

export type AgentLogCountKey = keyof typeof AGENT_LOG_COUNT_DEFS

/** PURE — does this log row fall under the definition? */
export function matchesAgentLogDef(
  def: AgentLogCountDef,
  log: { agentId?: unknown; taskType?: unknown; status?: unknown },
): boolean {
  if ('statusIn' in def) return typeof log.status === 'string' && def.statusIn.includes(log.status)
  return log.agentId === def.agentId && log.taskType === def.taskType
}

/** The Firestore constraints for the same definition (equality-only → no composite index). */
export function agentLogCountConstraints(def: AgentLogCountDef): QueryConstraint[] {
  if ('statusIn' in def) return [where('status', 'in', [...def.statusIn])]
  return [where('agentId', '==', def.agentId), where('taskType', '==', def.taskType)]
}

/** Stable spec map for `useServerCounts`. */
export const AGENT_LOG_COUNT_SPECS: Record<AgentLogCountKey, QueryConstraint[]> = Object.fromEntries(
  (Object.keys(AGENT_LOG_COUNT_DEFS) as AgentLogCountKey[])
    .map((k) => [k, agentLogCountConstraints(AGENT_LOG_COUNT_DEFS[k])]),
) as Record<AgentLogCountKey, QueryConstraint[]>

/**
 * PURE — the number to show. The server count when we have it; otherwise the
 * count over the loaded logs, which is exact only while fewer than `cap` logs
 * exist and a lower bound beyond that (shown only until the count returns).
 */
export function pickActivityCount(
  serverCount: number | null | undefined,
  loadedLogs: ReadonlyArray<{ agentId?: unknown; taskType?: unknown; status?: unknown }>,
  def: AgentLogCountDef,
): number {
  if (typeof serverCount === 'number') return serverCount
  return loadedLogs.filter((l) => matchesAgentLogDef(def, l)).length
}
